import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { fetchCourierProviderData } from "../../redux/slices/courier-provider/courierProviderActions";
import { setDeliveryMethod } from "../../redux/slices/delivery-method";
import { Message } from "primereact/message";

import Helpers from "../../utils/helpers";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { CriteriaChecker } from "../courier-provider/courier-criteria";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";

const TruckIcon = ({ size = 18 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" />
    </svg>
);

interface CourierOption {
    id: number;
    name: string;
    description: string;
    image: string;
    charges?: string;
    estimatedDays?: string;
    courierData?: any;
}

interface SidecartCourierProps {
    mode?: "PAGE" | "SIDECART";
    onBack: () => void;
    onNext: () => void;
    onClose: () => void;
}

const SidecartCourier: React.FC<SidecartCourierProps> = ({ onBack, onNext, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const [selectedMethod, setSelectedMethod] = useState<CourierOption | null>(null);
    const [options, setOptions] = useState<CourierOption[]>([]);
    const [error, setError] = useState<string | null>(null);

    const courierData = useAppSelector((state) => state.courierProviders.data);
    const selectedAddress = useAppSelector((state) => state.userAddress.selecedUserAddresses);

    const totalWeight = JSON.parse(localStorage.getItem("total-weight") || "0");
    const weightForCalc = Math.max(Math.ceil(totalWeight), 1);

    useEffect(() => {
        dispatch(fetchCourierProviderData(""));
    }, [dispatch]);

    useEffect(() => {
        if (!selectedAddress) return;
        setError(null);

        const products = ProductStorageHelpers.GetProductStorage() || [];
        const hasCourierUnavailable = products.some((p: ProductCheckoutModel) => p.courierAvailable === false);
        const allSupportHomeDelivery = products.every((p: ProductCheckoutModel) => p.isHomeDeliveryAvailable === true);
        const homeDeliveryAvailable = selectedAddress.homeDeliveryAvailable && allSupportHomeDelivery;

        let available: CourierOption[] = [];

        if (homeDeliveryAvailable) {
            available.push({
                id: 0,
                name: "Home Delivery",
                description: "Delivered directly to your doorstep",
                image: "",
                estimatedDays: "2-3 days"
            });
        } else if (!hasCourierUnavailable) {
            const filtered = (courierData || []).filter(item => 
                item.stateName === selectedAddress.stateName && 
                item.criteria === CriteriaChecker(totalWeight)
            );

            filtered.forEach(courier => {
                const charges = courier.amount * weightForCalc + courier.amount;
                available.push({
                    id: courier.courierProviderID,
                    name: courier.providerName,
                    description: "Fast and reliable delivery",
                    image: "", // Add logic if image mapping exists
                    charges: Helpers.formatAmountInINR(charges),
                    estimatedDays: "3-5 days",
                    courierData: courier
                });
            });
        }

        if (available.length === 0) {
            setError("No delivery options available for this location.");
        } else {
            setOptions(available);
            if (available.length === 1) handleSelect(available[0]);
        }
    }, [courierData, selectedAddress, totalWeight]);

    const handleSelect = (option: CourierOption) => {
        setSelectedMethod(option);
        dispatch(setDeliveryMethod({ id: option.id, name: option.name, estimatedDays: option.estimatedDays }));
        
        if (option.id !== 0 && option.courierData) {
            const charges = option.courierData.amount * weightForCalc + option.courierData.amount;
            localStorage.setItem("courier-provider", JSON.stringify({
                courierProviderId: option.courierData.courierProviderID,
                courierProviderName: option.courierData.providerName,
                weightSlab: option.courierData.criteria,
                charges: option.courierData.amount,
            }));
            localStorage.setItem("courier-charges", JSON.stringify(charges));
        } else {
            localStorage.removeItem("courier-provider");
            localStorage.setItem("courier-charges", JSON.stringify(0));
        }
    };

    return (
        <div className="flex flex-column h-full">
            <Toast ref={toast} />
            <div className="side-cart-header">
                <h5><i className="pi pi-truck text-xl" /> Delivery Method</h5>
                <div className="close-icon" onClick={onClose}>
                    <i className="pi pi-arrow-left" />
                </div>
            </div>

            <div className="side-cart-content text-sm">
                <div className="animate-in">
                    <span className="grey-label mb-3">Choose Delivery Option</span>
                    
                    {options.map((option) => (
                        <div 
                            key={option.id}
                            className={`address-card ${selectedMethod?.id === option.id ? 'selected' : ''}`}
                            onClick={() => handleSelect(option)}
                        >
                            <div className="address-top">
                                <span className="name">{option.name}</span>
                                {option.id === 0 ? (
                                    <span className="font-bold text-green-600">FREE</span>
                                ) : (
                                    <span className="font-bold text-gray-900">{option.charges}</span>
                                )}
                            </div>
                            <div className="address-details">
                                <div className="text-gray-600 mb-1">{option.description}</div>
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-clock" />
                                    <span>Estimated: {option.estimatedDays}</span>
                                </div>
                            </div>
                            {selectedMethod?.id === option.id && (
                                <div style={{ position: 'absolute', bottom: '1.25rem', right: '1.25rem' }}>
                                    <i className="pi pi-check-circle text-2xl text-green-500" />
                                </div>
                            )}
                        </div>
                    ))}

                    {error && (
                        <div className="text-center py-8 text-gray-400 bg-white rounded-lg border-2 border-dashed border-gray-200">
                            <i className="pi pi-exclamation-circle text-4xl mb-2" />
                            <p>{error}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="drawer-footer">
                <Button label="Back" outlined severity="secondary" className="btn-back" onClick={onBack} />
                <Button label="Continue" icon="pi pi-check" iconPos="right" disabled={!selectedMethod} className="btn-continue checkout-btn" onClick={onNext} />
            </div>
        </div>
    );
};

export default SidecartCourier;
